// src/pages/AccountPage.jsx
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../hooks/AuthContext";
import config from "../config";

const { API_BASE, SHOP_UPLOADS_URL } = config;

function formatPrice(val) {
  if (val === null || val === undefined) return "R0.00";
  return "R" + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

/* An order is only reviewable once it's been fully paid AND delivered.
 * Mirrors the same check enforced server-side in customer-review.php —
 * this is a UX gate only (hides/disables the button); the backend check
 * is what actually stops the request, so keep both in sync if the enum
 * values ever change. */
function isReviewEligible(order) {
  return (
    (order?.payment_status || "").toLowerCase() === "paid" &&
    (order?.status || "").toLowerCase() === "delivered"
  );
}

async function apiFetch(url, token, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

/* ─────────────────────────────────────────
   Logout Confirm Modal
───────────────────────────────────────── */
function LogoutModal({ onConfirm, onCancel }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  return (
    <div className="lm-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }} role="dialog" aria-modal="true" aria-label="Sign out confirmation">
      <div className="lm-modal">
        <div className="lm-icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </div>
        <h2 className="lm-title">Sign out?</h2>
        <p className="lm-body">You'll need to sign in again to access your account, orders and wishlist.</p>
        <div className="lm-actions">
          <button className="lm-btn lm-btn--cancel" onClick={onCancel}>Stay signed in</button>
          <button className="lm-btn lm-btn--confirm" onClick={onConfirm}>Yes, sign out</button>
        </div>
      </div>
      <style>{`
        .lm-backdrop{position:fixed;inset:0;z-index:9999;background:rgba(1,15,50,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'Roboto',sans-serif;}
        .lm-modal{background:#fff;border-radius:18px;padding:36px 32px;max-width:380px;width:100%;text-align:center;box-shadow:0 8px 40px rgba(1,15,50,.22);animation:lm-in .2s cubic-bezier(.34,1.3,.64,1);}
        @keyframes lm-in{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
        .lm-icon{width:60px;height:60px;border-radius:50%;background:#fef2f2;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;}
        .lm-title{font-size:20px;font-weight:800;color:#0f1c38;margin-bottom:10px;}
        .lm-body{font-size:14px;color:#6b7a99;line-height:1.6;margin-bottom:28px;}
        .lm-actions{display:flex;gap:10px;}
        .lm-btn{flex:1;padding:12px 16px;border-radius:10px;border:none;font-family:'Roboto',sans-serif;font-size:14px;font-weight:700;cursor:pointer;transition:all .18s;}
        .lm-btn--cancel{background:#f0f4fa;color:#374151;border:1.5px solid #e2e8f0;}
        .lm-btn--cancel:hover{background:#e4ebf5;color:#013d97;}
        .lm-btn--confirm{background:#dc2626;color:#fff;}
        .lm-btn--confirm:hover{background:#b91c1c;}
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────
   Star Rating (used inside ReviewModal)
───────────────────────────────────────── */
function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="rv-stars" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            className="rv-star"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(n)}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill={filled ? "#f59e0b" : "none"} stroke={filled ? "#f59e0b" : "#cbd5e1"} strokeWidth="1.5">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────
   Review Modal — submits to customer-review.php,
   which writes into product_reviews (linked to the
   specific order_id + product_id purchased).
───────────────────────────────────────── */
function ReviewModal({ item, token, onClose, onSubmitted }) {
  const [rating,    setRating]    = useState(0);
  const [content,   setContent]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error,     setError]     = useState(null);
  const [done,      setDone]      = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  async function handleSubmit() {
    if (rating < 1) { setError("Please select a star rating."); return; }
    if (content.trim().length < 5) { setError("Please write a few words about the product."); return; }
    setSubmitting(true); setError(null);
    try {
      await apiFetch(`${API_BASE}/customer-review.php`, token, {
        method: "POST",
        body: JSON.stringify({
          order_id:   item.orderId,
          product_id: item.product_id,
          rating,
          content: content.trim(),
        }),
      });
      setDone(true);
      onSubmitted?.(item.product_id);
      setTimeout(() => onClose(), 1400);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rv-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} role="dialog" aria-modal="true" aria-label="Write a review">
      <div className="rv-modal">
        {done ? (
          <div className="rv-done">
            <div className="rv-done__icon" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <p className="rv-done__text">Thanks! Your review is awaiting approval.</p>
          </div>
        ) : (
          <>
            <button className="rv-close" onClick={onClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <h2 className="rv-title">Review this product</h2>
            <p className="rv-product">{item.name}</p>

            {error && <div className="ac-msg ac-msg--error">{error}</div>}

            <div className="rv-field">
              <label className="ac-field__label">Your rating</label>
              <StarRating value={rating} onChange={setRating} />
            </div>

            <div className="rv-field">
              <label className="ac-field__label">Your review</label>
              <textarea
                className="rv-textarea"
                rows={4}
                maxLength={2000}
                placeholder="What did you think of this product?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

            <div className="rv-actions">
              <button className="ac-btn ac-btn--ghost" onClick={onClose} disabled={submitting}>Cancel</button>
              <button className="ac-btn ac-btn--primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <span className="ac-spinner" /> : null}
                {submitting ? "Submitting…" : "Submit review"}
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`
        .rv-backdrop{position:fixed;inset:0;z-index:9999;background:rgba(1,15,50,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'Roboto',sans-serif;}
        .rv-modal{position:relative;background:#fff;border-radius:18px;padding:32px 30px;max-width:420px;width:100%;box-shadow:0 8px 40px rgba(1,15,50,.22);animation:lm-in .2s cubic-bezier(.34,1.3,.64,1);}
        .rv-close{position:absolute;top:14px;right:14px;background:#f0f4fa;border:none;border-radius:8px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;color:#6b7a99;cursor:pointer;}
        .rv-close:hover{background:#e4ebf5;color:#013d97;}
        .rv-title{font-size:18px;font-weight:800;color:#0f1c38;margin-bottom:2px;padding-right:24px;}
        .rv-product{font-size:13px;color:#8895b0;margin-bottom:18px;}
        .rv-field{display:flex;flex-direction:column;gap:8px;margin-bottom:16px;}
        .rv-stars{display:flex;gap:4px;}
        .rv-star{background:none;border:none;padding:0;cursor:pointer;line-height:0;transition:transform .1s;}
        .rv-star:hover{transform:scale(1.1);}
        .rv-textarea{border:1.5px solid #e2e8f0;border-radius:10px;padding:11px 14px;font-family:'Roboto',sans-serif;font-size:14px;color:#0f1c38;background:#fafbfd;outline:none;resize:vertical;transition:border-color .18s,box-shadow .18s;}
        .rv-textarea:focus{border-color:#013d97;box-shadow:0 0 0 3px rgba(1,61,151,.1);background:#fff;}
        .rv-actions{display:flex;gap:10px;margin-top:6px;}
        .rv-actions .ac-btn{flex:1;}
        .rv-done{display:flex;flex-direction:column;align-items:center;text-align:center;gap:12px;padding:16px 0;}
        .rv-done__icon{width:56px;height:56px;border-radius:50%;background:#f0fdf4;display:flex;align-items:center;justify-content:center;}
        .rv-done__text{font-size:14.5px;font-weight:600;color:#166534;}
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────
   Tab: Profile
───────────────────────────────────────── */
function ProfileTab({ customer, token, onCustomerUpdate }) {
  const [form, setForm] = useState({
    first_name:  customer?.first_name  || "",
    last_name:   customer?.last_name   || "",
    phone:       customer?.phone       || "",
    address:     customer?.address     || "",
    city:        customer?.city        || "",
    province:    customer?.province    || "",
    postal_code: customer?.postal_code || "",
  });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving,   setSaving]   = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [msg,      setMsg]      = useState(null);
  const [pwMsg,    setPwMsg]    = useState(null);

  const set   = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const setPw = (k) => (v) => setPwForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    setSaving(true); setMsg(null);
    try {
      await apiFetch(`${API_BASE}/customer-profile.php`, token, { method: "PUT", body: JSON.stringify(form) });
      setMsg({ type: "success", text: "Profile updated successfully." });
      onCustomerUpdate({ ...customer, ...form });
    } catch (e) {
      setMsg({ type: "error", text: e.message });
    } finally { setSaving(false); }
  }

  async function handlePasswordChange() {
    if (pwForm.new_password !== pwForm.confirm) { setPwMsg({ type: "error", text: "New passwords do not match." }); return; }
    if (pwForm.new_password.length < 8)          { setPwMsg({ type: "error", text: "Password must be at least 8 characters." }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      await apiFetch(`${API_BASE}/customer-profile.php`, token, {
        method: "PUT",
        body: JSON.stringify({ current_password: pwForm.current_password, new_password: pwForm.new_password }),
      });
      setPwMsg({ type: "success", text: "Password changed successfully." });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      setPwMsg({ type: "error", text: e.message });
    } finally { setPwSaving(false); }
  }

  return (
    <div className="ac-profile">
      <div className="ac-card">
        <h2 className="ac-card__title">Personal Information</h2>
        {msg && <div className={`ac-msg ac-msg--${msg.type}`}>{msg.text}</div>}
        <div className="ac-grid2">
          <Field label="First name" value={form.first_name} onChange={set("first_name")} required />
          <Field label="Last name"  value={form.last_name}  onChange={set("last_name")}  required />
        </div>
        <Field label="Email" value={customer?.email || ""} disabled hint="Email address cannot be changed." />
        <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="+27 82 000 0000" />
        <h3 className="ac-card__sub">Delivery Address</h3>
        <Field label="Street address" value={form.address}     onChange={set("address")}     placeholder="12 Example Street" />
        <div className="ac-grid2">
          <Field label="City"     value={form.city}     onChange={set("city")}     placeholder="Cape Town" />
          <Field label="Province" value={form.province} onChange={set("province")} placeholder="Western Cape" />
        </div>
        <Field label="Postal code" value={form.postal_code} onChange={set("postal_code")} placeholder="8001" />
        <button className="ac-btn ac-btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? <span className="ac-spinner" /> : null}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="ac-card">
        <h2 className="ac-card__title">Change Password</h2>
        {pwMsg && <div className={`ac-msg ac-msg--${pwMsg.type}`}>{pwMsg.text}</div>}
        <Field label="Current password"     type="password" value={pwForm.current_password} onChange={setPw("current_password")} autoComplete="current-password" />
        <Field label="New password"         type="password" value={pwForm.new_password}     onChange={setPw("new_password")}     placeholder="Min 8 characters" autoComplete="new-password" />
        <Field label="Confirm new password" type="password" value={pwForm.confirm}          onChange={setPw("confirm")}          autoComplete="new-password" />
        <button className="ac-btn ac-btn--primary" onClick={handlePasswordChange} disabled={pwSaving}>
          {pwSaving ? <span className="ac-spinner" /> : null}
          {pwSaving ? "Updating…" : "Update password"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Tab: Orders — accordion + reorder + review
───────────────────────────────────────── */
function OrdersTab({ token, onAddToCart }) {
  const navigate = useNavigate();

  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [expanded,     setExpanded]     = useState(() => new Set());
  const [reorderMsg,   setReorderMsg]   = useState({});   // { [orderId]: string }
  const [reviewedKeys, setReviewedKeys] = useState(() => new Set()); // `${orderId}:${productId}`
  const [reviewItem,   setReviewItem]   = useState(null); // { orderId, product_id, name }

  useEffect(() => {
    apiFetch(`${API_BASE}/customer-orders.php`, token)
      .then(data => {
        const loadedOrders = data.orders || [];
        setOrders(loadedOrders);

        // Seed reviewedKeys from the server so the "Review submitted" badge
        // survives a page refresh. Relies on customer-orders.php returning
        // an `already_reviewed` flag per item (LEFT JOIN product_reviews on
        // order_id + product_id) — safe no-op if that field isn't present yet.
        const seeded = new Set();
        loadedOrders.forEach(o => (o.items || []).forEach(i => {
          if (i.already_reviewed) seeded.add(`${o.id}:${i.product_id}`);
        }));
        setReviewedKeys(seeded);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  function toggleExpanded(orderId) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });
  }

  // An order item can only be opened as a product page if the product still
  // exists (LEFT JOIN in customer-orders.php returns a slug) and hasn't been
  // deactivated — a deleted/discontinued product has no slug to route to.
  function isViewable(item) {
    return Boolean(item.slug) && item.product_active !== 0;
  }

  function goToProduct(item) {
    if (!isViewable(item)) return;
    navigate(`/shop/${item.slug}`);
  }

  // Adds every item from this order back to the cart at its original
  // quantity — no stock/active filtering. If a product has since been
  // deleted or gone out of stock, that's surfaced later at checkout;
  // Reorder itself always re-adds the full order as the customer bought it.
  function handleReorder(order) {
    const items = order.items || [];
    if (items.length === 0) return;

    items.forEach(item => {
      onAddToCart?.({
        id:     item.product_id,
        name:   item.name,
        price:  item.price,
        image1: item.image1,
        slug:   item.slug,
        sku:    item.sku,
        qty:    item.qty,
      });
    });

    setReorderMsg(m => ({
      ...m,
      [order.id]: `Added ${items.length} item${items.length > 1 ? "s" : ""} to your cart.`,
    }));
    setTimeout(() => {
      setReorderMsg(m => { const next = { ...m }; delete next[order.id]; return next; });
    }, 4000);
  }

  function handleReviewSubmitted(orderId, productId) {
    setReviewedKeys(prev => new Set(prev).add(`${orderId}:${productId}`));
  }

  if (loading) return <div className="ac-loading"><span className="ac-spinner ac-spinner--dark" />Loading orders…</div>;
  if (error)   return <div className="ac-empty-state"><p className="ac-empty-state__text">Could not load orders: {error}</p></div>;

  if (orders.length === 0) {
    return (
      <div className="ac-empty-state">
        <div className="ac-empty-state__icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
            <rect x="9" y="3" width="6" height="4" rx="1"/>
          </svg>
        </div>
        <p className="ac-empty-state__title">No orders yet</p>
        <p className="ac-empty-state__sub">Your order history will appear here.</p>
        <a href="/shop" className="ac-btn ac-btn--primary">Browse products</a>
      </div>
    );
  }

  return (
    <div className="ac-orders">
      {reviewItem && (
        <ReviewModal
          item={reviewItem}
          token={token}
          onClose={() => setReviewItem(null)}
          onSubmitted={(productId) => handleReviewSubmitted(reviewItem.orderId, productId)}
        />
      )}

      {orders.map(order => {
        const isOpen = expanded.has(order.id);
        const reviewEligible = isReviewEligible(order);
        return (
          <div key={order.id} className="ac-order-card">
            <button
              className="ac-order-card__header ac-order-card__header--btn"
              onClick={() => toggleExpanded(order.id)}
              aria-expanded={isOpen}
            >
              <div>
                <span className="ac-order-card__num">Order #{order.order_number || order.id}</span>
                <span className="ac-order-card__date">{formatDate(order.created_at)} &middot; {(order.items || []).length} item{(order.items || []).length !== 1 ? "s" : ""}</span>
              </div>
              <div className="ac-order-card__header-right">
                <span className={`ac-badge ac-badge--${(order.status || "pending").toLowerCase()}`}>{order.status || "Pending"}</span>
                <svg className={`ac-chevron ${isOpen ? "ac-chevron--open" : ""}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </button>

            {isOpen && (
              <div className="ac-order-card__items">
                {(order.items || []).map((item, i) => {
                  const key = `${order.id}:${item.product_id}`;
                  const alreadyReviewed = reviewedKeys.has(key);
                  const viewable = isViewable(item);
                  return (
                    <div
                      key={i}
                      className={`ac-order-item ${viewable ? "ac-order-item--clickable" : ""}`}
                      onClick={() => goToProduct(item)}
                      role={viewable ? "link" : undefined}
                      tabIndex={viewable ? 0 : undefined}
                      onKeyDown={viewable ? (e) => { if (e.key === "Enter") goToProduct(item); } : undefined}
                      aria-label={viewable ? `View ${item.name}` : undefined}
                      title={viewable ? "View product" : undefined}
                    >
                      <div className="ac-order-item__img">
                        {item.image1
                          ? <img src={`${SHOP_UPLOADS_URL}/${item.image1}`} alt={item.name} />
                          : <div className="ac-order-item__img-fallback"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/></svg></div>}
                      </div>
                      <div className="ac-order-item__info">
                        <span className="ac-order-item__name">{item.name}</span>
                        <span className="ac-order-item__meta">Qty: {item.qty} · {formatPrice(item.price)}</span>
                        {item.product_id && (
                          alreadyReviewed ? (
                            <span className="ac-order-item__reviewed">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                              Review submitted
                            </span>
                          ) : reviewEligible ? (
                            <button
                              className="ac-order-item__review-btn"
                              onClick={(e) => { e.stopPropagation(); setReviewItem({ orderId: order.id, product_id: item.product_id, name: item.name }); }}
                            >
                              Write a review
                            </button>
                          ) : (
                            <span className="ac-order-item__review-pending">
                              Review available once delivered
                            </span>
                          )
                        )}
                      </div>
                      <span className="ac-order-item__total">{formatPrice(item.qty * item.price)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="ac-order-card__footer">
              <div className="ac-order-card__footer-left">
                <span className="ac-order-card__total-label">Total</span>
                <span className="ac-order-card__total">{formatPrice(order.total)}</span>
              </div>
              <div className="ac-order-card__footer-right">
                {reorderMsg[order.id] && <span className="ac-reorder-msg">{reorderMsg[order.id]}</span>}
                <button
                  className="ac-btn ac-btn--ghost ac-btn--sm"
                  onClick={() => handleReorder(order)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                  Reorder
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────
   Tab: Wishlist — uses auth token, not localStorage
───────────────────────────────────────── */
function WishlistTab({ token, onAddToCart }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const loadWishlist = useCallback(() => {
    setLoading(true);
    apiFetch(`${API_BASE}/customer-wishlist.php`, token)
      .then(data => setItems(data.wishlist || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadWishlist(); }, [loadWishlist]);

  async function handleRemove(productId) {
    try {
      await apiFetch(`${API_BASE}/customer-wishlist.php`, token, {
        method: "DELETE",
        body: JSON.stringify({ product_id: productId }),
      });
      setItems(prev => prev.filter(i => i.id !== productId));
    } catch (e) {
      alert("Could not remove item: " + e.message);
    }
  }

  if (loading) return <div className="ac-loading"><span className="ac-spinner ac-spinner--dark" />Loading wishlist…</div>;
  if (error)   return <div className="ac-empty-state"><p className="ac-empty-state__text">Could not load wishlist: {error}</p></div>;

  if (items.length === 0) {
    return (
      <div className="ac-empty-state">
        <div className="ac-empty-state__icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </div>
        <p className="ac-empty-state__title">Your wishlist is empty</p>
        <p className="ac-empty-state__sub">Save products you love and find them here.</p>
        <a href="/shop" className="ac-btn ac-btn--primary">Browse products</a>
      </div>
    );
  }

  return (
    <div className="ac-wishlist">
      {items.map(item => (
        <div key={item.id} className="ac-wish-card">
          <a href={`/shop/${item.slug}`} className="ac-wish-card__img-wrap">
            {item.image1
              ? <img src={`${SHOP_UPLOADS_URL}/${item.image1}`} alt={item.name} className="ac-wish-card__img" />
              : <div className="ac-wish-card__img-fallback"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="3" y="3" width="18" height="18" rx="2"/></svg></div>}
          </a>
          <div className="ac-wish-card__body">
            {item.brand && <p className="ac-wish-card__brand">{item.brand}</p>}
            <a href={`/shop/${item.slug}`} className="ac-wish-card__name">{item.name}</a>
            <div className="ac-wish-card__pricing">
              {item.sale_price
                ? <><span className="ac-wish-card__sale">{formatPrice(item.sale_price)}</span><span className="ac-wish-card__was">{formatPrice(item.price)}</span></>
                : <span className="ac-wish-card__price">{formatPrice(item.price)}</span>}
            </div>
          </div>
          <div className="ac-wish-card__actions">
            <button
              className="ac-btn ac-btn--primary ac-btn--sm"
              onClick={() => onAddToCart?.({ ...item, id: item.id, qty: 1 })}
              disabled={!item.quantity || item.quantity < 1}
            >
              {!item.quantity || item.quantity < 1 ? "Out of stock" : "Add to cart"}
            </button>
            <button
              className="ac-btn ac-btn--ghost ac-btn--sm ac-btn--icon"
              onClick={() => handleRemove(item.id)}
              aria-label="Remove from wishlist"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6M14 11v6"/>
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────
   Reusable Field
───────────────────────────────────────── */
function Field({ label, type = "text", value, onChange, placeholder, required, disabled, hint, autoComplete }) {
  return (
    <div className="ac-field">
      <label className="ac-field__label">
        {label}{required && <span className="ac-field__req"> *</span>}
      </label>
      <input
        className={`ac-field__input ${disabled ? "ac-field__input--disabled" : ""}`}
        type={type}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        readOnly={disabled}
      />
      {hint && <p className="ac-field__hint">{hint}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN AccountPage
───────────────────────────────────────── */
export default function AccountPage({ onAddToCart }) {
  const { token, customer, isLoggedIn, login, logout } = useAuthContext();
  const navigate = useNavigate();

  const params  = new URLSearchParams(window.location.search);
  const initTab = ["profile", "orders", "wishlist"].includes(params.get("tab")) ? params.get("tab") : "profile";
  const [activeTab,    setActiveTab]    = useState(initTab);
  const [showLogout,   setShowLogout]   = useState(false);
  const [authChecked,  setAuthChecked]  = useState(false);

  useEffect(() => { setAuthChecked(true); }, []);

  useEffect(() => {
    if (authChecked && !isLoggedIn) navigate("/shop");
  }, [authChecked, isLoggedIn, navigate]);

  function switchTab(tab) {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState({}, "", url.toString());
  }

  function handleCustomerUpdate(updated) {
    login(token, updated);
  }

  // Logout: clear auth then go to /shop
  function handleLogoutConfirm() {
    logout();
    navigate("/shop");
  }

  if (!authChecked || !isLoggedIn || !customer) return null;

  const tabs = [
    { id: "profile",  label: "Profile",
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
    { id: "orders",   label: "Orders",
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg> },
    { id: "wishlist", label: "Wishlist",
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  ];

  return (
    <div className="ac-page">
      {showLogout && (
        <LogoutModal
          onConfirm={handleLogoutConfirm}
          onCancel={() => setShowLogout(false)}
        />
      )}

      <div className="ac-hero">
        <div className="ac-hero__inner">
          <div className="ac-hero__avatar">{customer.first_name?.[0]?.toUpperCase() ?? "U"}</div>
          <div className="ac-hero__info">
            <h1 className="ac-hero__name">{customer.first_name} {customer.last_name}</h1>
            <p className="ac-hero__email">{customer.email}</p>
          </div>
          <button className="ac-hero__logout" onClick={() => setShowLogout(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign out
          </button>
        </div>
      </div>

      <div className="ac-layout">
        <aside className="ac-sidebar">
          <nav className="ac-tabs" aria-label="Account sections">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`ac-tab ${activeTab === t.id ? "ac-tab--active" : ""}`}
                onClick={() => switchTab(t.id)}
                aria-current={activeTab === t.id ? "page" : undefined}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="ac-content">
          {activeTab === "profile"  && <ProfileTab  customer={customer} token={token} onCustomerUpdate={handleCustomerUpdate} />}
          {activeTab === "orders"   && <OrdersTab   token={token} onAddToCart={onAddToCart} />}
          {activeTab === "wishlist" && <WishlistTab token={token} onAddToCart={onAddToCart} />}
        </main>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800&display=swap');
        .ac-page { font-family:'Roboto',sans-serif;min-height:100vh;background:#f5f7fb; }
        .ac-hero { background:linear-gradient(135deg,#013d97 0%,#0255cc 100%);padding:36px 0; }
        .ac-hero__inner { max-width:1100px;margin:0 auto;padding:0 24px;display:flex;align-items:center;gap:20px;flex-wrap:wrap; }
        .ac-hero__avatar { width:64px;height:64px;border-radius:50%;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:800;color:#fff;border:3px solid rgba(255,255,255,.35);flex-shrink:0; }
        .ac-hero__info { flex:1;min-width:0; }
        .ac-hero__name { font-size:22px;font-weight:800;color:#fff;letter-spacing:-.02em; }
        .ac-hero__email { font-size:14px;color:rgba(255,255,255,.7);margin-top:2px; }
        .ac-hero__logout { display:flex;align-items:center;gap:8px;padding:9px 18px;background:rgba(255,255,255,.12);border:1.5px solid rgba(255,255,255,.25);border-radius:8px;color:#fff;font-family:'Roboto',sans-serif;font-size:13.5px;font-weight:700;cursor:pointer;transition:background .18s;flex-shrink:0; }
        .ac-hero__logout:hover { background:rgba(220,38,38,.5);border-color:rgba(220,38,38,.6); }
        .ac-layout { max-width:1100px;margin:0 auto;padding:32px 24px;display:grid;grid-template-columns:220px 1fr;gap:28px;align-items:start; }
        @media(max-width:768px){ .ac-layout{grid-template-columns:1fr;padding:20px 16px;} }
        .ac-sidebar { position:sticky;top:110px; }
        .ac-tabs { display:flex;flex-direction:column;gap:4px;background:#fff;border-radius:14px;padding:10px;box-shadow:0 1px 4px rgba(1,15,50,.07); }
        @media(max-width:768px){ .ac-sidebar{position:static;} .ac-tabs{flex-direction:row;overflow-x:auto;} }
        .ac-tab { display:flex;align-items:center;gap:10px;padding:11px 14px;border:none;background:none;border-radius:8px;font-family:'Roboto',sans-serif;font-size:14px;font-weight:600;color:#6b7a99;cursor:pointer;transition:background .15s,color .15s;white-space:nowrap;text-align:left;width:100%; }
        .ac-tab svg { flex-shrink:0; }
        .ac-tab:hover { background:#f0f4fa;color:#013d97; }
        .ac-tab--active { background:#013d97;color:#fff; }
        .ac-tab--active svg { stroke:#fff; }
        @media(max-width:768px){ .ac-tab{width:auto;flex:1;justify-content:center;} }
        .ac-content { display:flex;flex-direction:column;gap:24px; }
        .ac-card { background:#fff;border-radius:14px;padding:28px;box-shadow:0 1px 4px rgba(1,15,50,.07);display:flex;flex-direction:column;gap:18px; }
        .ac-card__title { font-size:17px;font-weight:800;color:#0f1c38;letter-spacing:-.02em; }
        .ac-card__sub { font-size:14px;font-weight:700;color:#374151;padding-top:4px;border-top:1px solid #f0f3f8; }
        .ac-grid2 { display:grid;grid-template-columns:1fr 1fr;gap:14px; }
        @media(max-width:520px){ .ac-grid2{grid-template-columns:1fr;} }
        .ac-field { display:flex;flex-direction:column;gap:6px; }
        .ac-field__label { font-size:12px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.03em; }
        .ac-field__req { color:#ef4444; }
        .ac-field__input { border:1.5px solid #e2e8f0;border-radius:10px;padding:11px 14px;font-family:'Roboto',sans-serif;font-size:14px;color:#0f1c38;background:#fafbfd;outline:none;transition:border-color .18s,box-shadow .18s; }
        .ac-field__input:focus { border-color:#013d97;box-shadow:0 0 0 3px rgba(1,61,151,.1);background:#fff; }
        .ac-field__input--disabled { background:#f5f7fb;color:#8895b0;cursor:not-allowed; }
        .ac-field__hint { font-size:12px;color:#9ca3af; }
        .ac-msg { padding:11px 14px;border-radius:10px;font-size:13px;font-weight:600; }
        .ac-msg--success { background:#f0fdf4;border:1px solid #bbf7d0;color:#166534; }
        .ac-msg--error   { background:#fef2f2;border:1px solid #fecaca;color:#991b1b; }
        .ac-btn { display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:11px 22px;border:none;border-radius:10px;font-family:'Roboto',sans-serif;font-size:14px;font-weight:700;cursor:pointer;transition:all .18s;text-decoration:none; }
        .ac-btn:disabled { opacity:.5;cursor:not-allowed; }
        .ac-btn--primary { background:linear-gradient(135deg,#013d97,#0255cc);color:#fff;box-shadow:0 2px 8px rgba(1,61,151,.25); }
        .ac-btn--primary:hover:not(:disabled) { background:linear-gradient(135deg,#012f77,#0147aa);transform:translateY(-1px); }
        .ac-btn--ghost { background:#f0f4fa;color:#5a6a8a;border:1.5px solid #e2e8f0; }
        .ac-btn--ghost:hover:not(:disabled) { background:#e4ebf5;color:#013d97; }
        .ac-btn--sm { padding:8px 14px;font-size:13px; }
        .ac-btn--icon { padding:8px 10px; }
        .ac-spinner { width:14px;height:14px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:ac-spin .6s linear infinite;flex-shrink:0; }
        .ac-spinner--dark { border-color:rgba(1,61,151,.2);border-top-color:#013d97; }
        @keyframes ac-spin { to{transform:rotate(360deg)} }
        .ac-loading { display:flex;align-items:center;gap:12px;padding:48px 24px;color:#6b7a99;font-size:14px;font-weight:600; }
        .ac-empty-state { display:flex;flex-direction:column;align-items:center;gap:14px;padding:64px 24px;text-align:center;background:#fff;border-radius:14px;box-shadow:0 1px 4px rgba(1,15,50,.07); }
        .ac-empty-state__icon { width:72px;height:72px;border-radius:50%;background:#f0f4fa;display:flex;align-items:center;justify-content:center;color:#b8c2d8; }
        .ac-empty-state__title { font-size:17px;font-weight:800;color:#0f1c38; }
        .ac-empty-state__sub { font-size:13.5px;color:#8895b0;max-width:280px;line-height:1.55; }
        .ac-empty-state__text { font-size:13.5px;color:#991b1b; }
        .ac-profile { display:flex;flex-direction:column;gap:24px; }
        .ac-orders { display:flex;flex-direction:column;gap:18px; }
        .ac-order-card { background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 1px 4px rgba(1,15,50,.07); }
        .ac-order-card__header { display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #f0f3f8;gap:12px;flex-wrap:wrap; }
        .ac-order-card__header--btn { width:100%;background:none;border:none;border-bottom:1px solid #f0f3f8;cursor:pointer;text-align:left;font-family:'Roboto',sans-serif;transition:background .15s; }
        .ac-order-card__header--btn:hover { background:#fafbfd; }
        .ac-order-card__header-right { display:flex;align-items:center;gap:12px;flex-shrink:0; }
        .ac-chevron { color:#8895b0;transition:transform .2s;flex-shrink:0; }
        .ac-chevron--open { transform:rotate(180deg); }
        .ac-order-card__num { font-size:15px;font-weight:800;color:#0f1c38;display:block; }
        .ac-order-card__date { font-size:12px;color:#8895b0; }
        .ac-order-card__items { padding:8px 0; }
        .ac-order-card__footer { display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-top:1px solid #f0f3f8;background:#fafbfd;gap:14px;flex-wrap:wrap; }
        .ac-order-card__footer-left { display:flex;align-items:center;gap:8px; }
        .ac-order-card__footer-right { display:flex;align-items:center;gap:12px;flex-wrap:wrap;justify-content:flex-end; }
        .ac-order-card__total-label { font-size:13px;font-weight:600;color:#6b7a99; }
        .ac-order-card__total { font-size:18px;font-weight:900;color:#013d97; }
        .ac-reorder-msg { font-size:12.5px;color:#166534;font-weight:600;max-width:220px;line-height:1.4; }
        .ac-order-item { display:flex;align-items:center;gap:14px;padding:12px 20px;border-bottom:1px solid #f5f7fb; }
        .ac-order-item:last-child { border-bottom:none; }
        .ac-order-item--clickable { cursor:pointer;transition:background .15s; }
        .ac-order-item--clickable:hover { background:#fafbfd; }
        .ac-order-item--clickable:focus-visible { outline:2px solid #029add;outline-offset:-2px; }
        .ac-order-item__img { width:52px;height:52px;border-radius:8px;overflow:hidden;background:#f0f4fa;flex-shrink:0;border:1px solid #e8ecf3;display:flex;align-items:center;justify-content:center; }
        .ac-order-item__img img { width:100%;height:100%;object-fit:cover; }
        .ac-order-item__img-fallback { color:#b8c2d8; }
        .ac-order-item__info { flex:1;min-width:0;display:flex;flex-direction:column;gap:2px; }
        .ac-order-item__name { font-size:13.5px;font-weight:700;color:#0f1c38;display:block; }
        .ac-order-item--clickable:hover .ac-order-item__name { color:#013d97; }
        .ac-order-item__meta { font-size:12px;color:#8895b0; }
        .ac-order-item__total { font-size:14px;font-weight:800;color:#013d97;flex-shrink:0; }
        .ac-order-item__review-btn { align-self:flex-start;background:none;border:none;padding:2px 0 0;font-family:'Roboto',sans-serif;font-size:12px;font-weight:700;color:#013d97;cursor:pointer;text-decoration:underline;text-underline-offset:2px; }
        .ac-order-item__review-btn:hover { color:#012f77; }
        .ac-order-item__reviewed { display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:700;color:#16a34a;margin-top:2px; }
        .ac-order-item__review-pending { display:inline-block;font-size:11.5px;font-weight:600;color:#9ca3af;margin-top:2px; }
        .ac-badge { display:inline-flex;align-items:center;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:700;text-transform:capitalize; }
        .ac-badge--pending    { background:#fef9c3;color:#854d0e; }
        .ac-badge--confirmed  { background:#dbeafe;color:#1d4ed8; }
        .ac-badge--processing { background:#dbeafe;color:#1e40af; }
        .ac-badge--shipped    { background:#e0f2fe;color:#0369a1; }
        .ac-badge--delivered  { background:#dcfce7;color:#166534; }
        .ac-badge--cancelled  { background:#fee2e2;color:#991b1b; }
        .ac-badge--refunded   { background:#fae8ff;color:#7e22ce; }
        .ac-wishlist { display:flex;flex-direction:column;gap:14px; }
        .ac-wish-card { background:#fff;border-radius:14px;box-shadow:0 1px 4px rgba(1,15,50,.07);display:flex;align-items:center;gap:16px;padding:14px 18px;flex-wrap:wrap; }
        .ac-wish-card__img-wrap { width:80px;height:80px;border-radius:10px;overflow:hidden;background:#f0f4fa;flex-shrink:0;border:1px solid #e8ecf3;display:flex;align-items:center;justify-content:center; }
        .ac-wish-card__img { width:100%;height:100%;object-fit:cover; }
        .ac-wish-card__img-fallback { color:#b8c2d8; }
        .ac-wish-card__body { flex:1;min-width:0;display:flex;flex-direction:column;gap:4px; }
        .ac-wish-card__brand { font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#029add; }
        .ac-wish-card__name { font-size:14px;font-weight:700;color:#0f1c38;text-decoration:none;transition:color .15s; }
        .ac-wish-card__name:hover { color:#013d97; }
        .ac-wish-card__pricing { display:flex;align-items:center;gap:8px;margin-top:2px; }
        .ac-wish-card__price { font-size:15px;font-weight:800;color:#013d97; }
        .ac-wish-card__sale { font-size:15px;font-weight:800;color:#dc2626; }
        .ac-wish-card__was { font-size:13px;color:#9ca3af;text-decoration:line-through; }
        .ac-wish-card__actions { display:flex;align-items:center;gap:8px;flex-shrink:0; }
        .ac-page *:focus-visible { outline:2px solid #029add;outline-offset:2px; }
        @media(prefers-reduced-motion:reduce){ .ac-page *,.ac-page *::before,.ac-page *::after{transition-duration:0ms!important;animation-duration:0ms!important;} }
      `}</style>
    </div>
  );
}